// src/utils/generateWbrPack.js
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { getWbrPackData } from "shared/api/function_data";

const MARGIN = 40;
const PAGE_WIDTH = 595; // A4
const PAGE_HEIGHT = 842;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const COLORS = {
  dark: rgb(0.13, 0.13, 0.15),
  medium: rgb(0.35, 0.35, 0.4),
  light: rgb(0.55, 0.55, 0.6),
  accent: rgb(0.1, 0.35, 0.65),
  accentLight: rgb(0.92, 0.95, 1),
  success: rgb(0.06, 0.52, 0.35),
  warning: rgb(0.6, 0.4, 0.0),
  danger: rgb(0.8, 0.2, 0.2),
  border: rgb(0.85, 0.87, 0.9),
  bgGray: rgb(0.96, 0.97, 0.98),
  white: rgb(1, 1, 1),
  strikethrough: rgb(0.7, 0.7, 0.7),
};

function sanitize(text) {
  if (!text) return "";
  return text.replace(/\n/g, " ").replace(/\r/g, "").replace(/\t/g, " ").replace(/[^\x20-\xFF]/g, "").replace(/\s+/g, " ").trim();
}

export async function generateWbrPack({ functionId, year, week, functionConfig, onProgress }) {
  const notify = onProgress || (() => {});
  const details = functionConfig?.function_details || {};
  const sidebar = functionConfig?.function_sidebar || {};
  const pocData = functionConfig?.function_poc || {};

  const reportKeys = [];
  Object.values(sidebar).forEach((section) => {
    (section.items || []).forEach((item) => {
      if (item.type === "report") reportKeys.push({ key: item.key, label: item.label });
    });
  });

  // ─── Fetch ALL data in a SINGLE API call ───
  notify("Fetching all data (1 API call)...");
  let packData;
  try {
    packData = await getWbrPackData(functionId, year, week, reportKeys.map((r) => r.key));
  } catch (err) {
    throw new Error(`Failed to fetch WBR data: ${err.message}`);
  }

  const businessUpdates = packData.business_updates || { updates: [] };
  const actions = packData.action_tracker?.actions || [];

  // Build report data from bulk response
  notify("Processing reports...");
  const reportData = [];
  for (const { key, label } of reportKeys) {
    const pdfEntry = packData.report_pdfs?.[key];
    const calloutEntry = packData.callouts?.[key];

    let pdfUrl = null;
    if (pdfEntry?.status === "success" && pdfEntry.url) {
      pdfUrl = pdfEntry.url; // presigned S3 URL
    }

    let calloutText = null;
    if (calloutEntry?.status === "success" && calloutEntry.callout) {
      let parsed = calloutEntry.callout;
      if (typeof parsed === "string") {
        try { const obj = JSON.parse(parsed.replace(/'/g, '"')); if (obj?.text) parsed = obj.text; } catch (e) {
          const m = parsed.match(/['"]text['"]\s*:\s*['"]([\s\S]+?)['"]\s*,\s*['"]createdAt/);
          if (m) parsed = m[1];
        }
      }
      if (typeof parsed === "object" && parsed.text) parsed = parsed.text;
      if (typeof parsed === "string") calloutText = parsed.replace(/\\n/g, "\n");
    }

    reportData.push({ key, label, pdfUrl, calloutText });
  }

  // ─── Build PDF ───
  notify("Building PDF...");
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  function drawWrapped(page, text, x, y, opts = {}) {
    const { maxWidth = CONTENT_WIDTH, fontSize = 9, lineHeight = 12, textFont = font, color = COLORS.dark } = opts;
    const safe = sanitize(text);
    const words = safe.split(" ");
    let line = "";
    let cy = y;
    for (const word of words) {
      if (!word) continue;
      const test = line ? `${line} ${word}` : word;
      if (textFont.widthOfTextAtSize(test, fontSize) > maxWidth && line) {
        page.drawText(line, { x, y: cy, font: textFont, size: fontSize, color });
        cy -= lineHeight;
        line = word;
      } else { line = test; }
    }
    if (line) { page.drawText(line, { x, y: cy, font: textFont, size: fontSize, color }); cy -= lineHeight; }
    return cy;
  }

  // Counts lines for height estimation
  function countLines(text, fontSize, maxWidth) {
    const safe = sanitize(text);
    const words = safe.split(" ");
    let lines = 1, line = "";
    for (const word of words) {
      if (!word) continue;
      const test = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(test, fontSize) > maxWidth && line) { lines++; line = word; }
      else line = test;
    }
    return lines;
  }

  // ═══════════════════════════════════════════
  // 1. COVER PAGE — Auto-sized
  // ═══════════════════════════════════════════
  notify("Creating cover page...");
  {
    const pocCount = Object.keys(pocData).length;
    const pageH = Math.max(200 + (pocCount * 12) + 50, 300);
    const page = doc.addPage([PAGE_WIDTH, pageH]);
    page.drawRectangle({ x: 0, y: pageH - 5, width: PAGE_WIDTH, height: 5, color: COLORS.accent });

    let y = pageH - 35;
    page.drawText("WEEKLY BUSINESS REVIEW", { x: MARGIN, y, font: fontBold, size: 8, color: COLORS.accent });
    y -= 18;
    page.drawText(sanitize(details.full_name || "Business Review"), { x: MARGIN, y, font: fontBold, size: 16, color: COLORS.dark });
    y -= 16;
    page.drawText(`Week ${details.week || week}, ${year} | ${sanitize(details.daterange?.display || "")}`, { x: MARGIN, y, font, size: 8, color: COLORS.light });
    y -= 20;

    // Meeting roles inline
    page.drawRectangle({ x: MARGIN, y: y - 40, width: CONTENT_WIDTH, height: 48, color: COLORS.bgGray });
    page.drawText("Meeting Roles", { x: MARGIN + 8, y: y - 2, font: fontBold, size: 7, color: COLORS.accent });
    y -= 13;
    [["Coord", details.meeting_coordination?.login], ["Facil", details.meeting_facilitator?.login], ["Notes", details.meeting_note_taker?.login]].forEach(([l, v]) => {
      page.drawText(`${l}:`, { x: MARGIN + 8, y, font: fontBold, size: 7, color: COLORS.medium });
      page.drawText(sanitize(v || "N/A"), { x: MARGIN + 45, y, font, size: 7, color: COLORS.dark });
      y -= 10;
    });
    y -= 15;

    // POC — ALL CAPS program names
    if (pocCount > 0) {
      page.drawText("PROGRAM POC", { x: MARGIN, y, font: fontBold, size: 8, color: COLORS.accent });
      y -= 12;
      const colX = [MARGIN, MARGIN + 70, MARGIN + 175, MARGIN + 290];
      page.drawRectangle({ x: MARGIN, y: y - 1, width: CONTENT_WIDTH, height: 11, color: COLORS.accentLight });
      page.drawText("PROGRAM", { x: colX[0] + 2, y, font: fontBold, size: 6, color: COLORS.accent });
      page.drawText("SLIDE POC", { x: colX[1] + 2, y, font: fontBold, size: 6, color: COLORS.accent });
      page.drawText("WFM POC", { x: colX[2] + 2, y, font: fontBold, size: 6, color: COLORS.accent });
      page.drawText("OPS POC", { x: colX[3] + 2, y, font: fontBold, size: 6, color: COLORS.accent });
      y -= 12;
      Object.entries(pocData).forEach(([key, entry]) => {
        page.drawText(sanitize(entry.label || key).toUpperCase(), { x: colX[0] + 2, y, font: fontBold, size: 6, color: COLORS.dark });
        page.drawText(sanitize(entry.slide_poc?.primary || "-"), { x: colX[1] + 2, y, font, size: 6, color: COLORS.medium });
        page.drawText(sanitize(entry.wfm_poc?.primary || "-"), { x: colX[2] + 2, y, font, size: 6, color: COLORS.medium });
        page.drawText(sanitize(entry.ops_poc?.primary || "-"), { x: colX[3] + 2, y, font, size: 6, color: COLORS.medium });
        y -= 10;
      });
    }
    page.drawRectangle({ x: 0, y: 0, width: PAGE_WIDTH, height: 14, color: COLORS.bgGray });
    page.drawText("Amazon Confidential | Internal Use Only", { x: MARGIN, y: 4, font, size: 6, color: COLORS.light });
  }

  // ═══════════════════════════════════════════
  // 2. BUSINESS UPDATES — Auto-sized pages (no whitespace)
  // ═══════════════════════════════════════════
  notify("Adding business updates...");
  {
    const updates = businessUpdates.updates || [];
    const fields = [
      { key: "success", label: "[+] Success", color: COLORS.success },
      { key: "opportunities", label: "[!] Opportunities", color: COLORS.warning },
      { key: "helpNeeded", label: "[*] Help Needed", color: COLORS.danger },
      { key: "nextSteps", label: "[-] Next Steps", color: COLORS.accent },
    ];

    // Pre-calculate total content height to make auto-sized page
    let totalH = 40; // header
    for (const update of updates) {
      for (const seg of (update.segments || [])) {
        totalH += 16; // title
        for (const f of fields) {
          const c = seg[f.key];
          if (c && c.trim()) { totalH += 12 + (countLines(c, 8, CONTENT_WIDTH - 10) * 11) + 6; }
        }
        totalH += 14; // separator
      }
    }
    if (updates.length === 0) totalH += 20;
    const buPageH = Math.min(Math.max(totalH + 30, 150), PAGE_HEIGHT);

    let page = doc.addPage([PAGE_WIDTH, buPageH]);
    let y = buPageH - 30;
    page.drawRectangle({ x: 0, y: buPageH - 4, width: PAGE_WIDTH, height: 4, color: COLORS.accent });
    page.drawText("BUSINESS UPDATES", { x: MARGIN, y, font: fontBold, size: 11, color: COLORS.dark });
    page.drawText(`Week ${week}, ${year}`, { x: PAGE_WIDTH - MARGIN - 70, y, font, size: 8, color: COLORS.light });
    y -= 15;
    page.drawRectangle({ x: MARGIN, y, width: CONTENT_WIDTH, height: 0.5, color: COLORS.border });
    y -= 12;

    if (updates.length === 0) {
      page.drawText("No business updates for this week.", { x: MARGIN, y, font, size: 8, color: COLORS.light });
    } else {
      for (const update of updates) {
        for (const seg of (update.segments || [])) {
          if (y < 50) { page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]); y = PAGE_HEIGHT - 40; }
          page.drawText(sanitize(seg.title || "Untitled"), { x: MARGIN, y, font: fontBold, size: 9, color: COLORS.dark });
          y -= 13;
          for (const field of fields) {
            const content = seg[field.key];
            if (!content || !content.trim()) continue;
            if (y < 40) { page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]); y = PAGE_HEIGHT - 40; }
            page.drawCircle({ x: MARGIN + 3, y: y + 2, size: 2, color: field.color, opacity: 1 });
            page.drawText(field.label, { x: MARGIN + 8, y, font: fontBold, size: 7, color: field.color });
            y -= 10;
            y = drawWrapped(page, content, MARGIN + 8, y, { fontSize: 8, lineHeight: 11, color: COLORS.medium, maxWidth: CONTENT_WIDTH - 8 });
            y -= 5;
          }
          if (y > MARGIN) { page.drawRectangle({ x: MARGIN, y, width: CONTENT_WIDTH, height: 0.3, color: COLORS.border }); y -= 10; }
        }
      }
    }
  }

  // ═══════════════════════════════════════════
  // 3. ACTION TRACKER — Full detail cards, ECD history with strikethrough, full comments
  // ═══════════════════════════════════════════
  notify("Adding action tracker...");
  {
    const activeActions = actions.filter((a) => a.status !== "Completed");

    // Estimate height per action
    let totalH = 35;
    for (const action of activeActions) {
      let cardH = 55; // base (title + owner/ecd/status + action_item)
      if (action.action_item) cardH += Math.max(countLines(action.action_item, 7, CONTENT_WIDTH - 16) * 9, 9);
      if (action.slide_name || action.line_item) cardH += 10;
      const comments = action.comments || [];
      if (comments.length > 0) cardH += 12 + Math.min(comments.length, 3) * 10;
      if (action.ecd_history && action.ecd_history.length > 0) cardH += 5;
      totalH += cardH + 8;
    }
    if (activeActions.length === 0) totalH += 20;
    const atPageH = Math.min(Math.max(totalH + 30, 150), PAGE_HEIGHT);

    let page = doc.addPage([PAGE_WIDTH, atPageH]);
    let y = atPageH - 30;
    page.drawRectangle({ x: 0, y: atPageH - 4, width: PAGE_WIDTH, height: 4, color: COLORS.accent });
    page.drawText("ACTION TRACKER", { x: MARGIN, y, font: fontBold, size: 11, color: COLORS.dark });
    page.drawText(`${activeActions.length} active / ${actions.length} total`, { x: PAGE_WIDTH - MARGIN - 95, y, font, size: 8, color: COLORS.light });
    y -= 15;
    page.drawRectangle({ x: MARGIN, y, width: CONTENT_WIDTH, height: 0.5, color: COLORS.border });
    y -= 12;

    if (activeActions.length === 0) {
      page.drawText("No active action items.", { x: MARGIN, y, font, size: 8, color: COLORS.light });
    } else {
      for (let idx = 0; idx < activeActions.length; idx++) {
        const action = activeActions[idx];
        if (y < 90) { page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]); y = PAGE_HEIGHT - 40; }

        // Title + status
        const titleText = sanitize(action.title || "Untitled");
        page.drawText(`${idx + 1}. ${titleText}`, { x: MARGIN + 4, y, font: fontBold, size: 8, color: COLORS.dark });
        const statusColor = action.status === "Open" ? COLORS.warning : action.status === "In Progress" ? COLORS.accent : action.status === "Blocked" ? COLORS.danger : COLORS.medium;
        page.drawText(`[${sanitize(action.status || "Open")}]`, { x: PAGE_WIDTH - MARGIN - 55, y, font: fontBold, size: 7, color: statusColor });
        y -= 12;

        // Owner + ECD with strikethrough history
        page.drawText(`Owner: ${sanitize(action.owner || "-")}`, { x: MARGIN + 4, y, font, size: 7, color: COLORS.medium });

        // ECD with history (strikethrough old dates)
        let ecdX = MARGIN + 130;
        page.drawText("ECD:", { x: ecdX, y, font: fontBold, size: 7, color: COLORS.medium });
        ecdX += 22;
        const ecdHistory = action.ecd_history || [];
        for (const oldDate of ecdHistory) {
          const oldText = sanitize(oldDate);
          const oldWidth = font.widthOfTextAtSize(oldText, 7);
          page.drawText(oldText, { x: ecdX, y, font, size: 7, color: COLORS.strikethrough });
          // Draw strikethrough line
          page.drawLine({ start: { x: ecdX, y: y + 3 }, end: { x: ecdX + oldWidth, y: y + 3 }, thickness: 0.5, color: COLORS.danger });
          ecdX += oldWidth + 6;
        }
        // Current ECD
        page.drawText(sanitize(action.ecd || "-"), { x: ecdX, y, font: fontBold, size: 7, color: COLORS.dark });
        y -= 11;

        // Action item — FULL text (wrapped)
        if (action.action_item) {
          page.drawText("Action:", { x: MARGIN + 4, y, font: fontBold, size: 7, color: COLORS.dark });
          y -= 9;
          y = drawWrapped(page, action.action_item, MARGIN + 8, y, { fontSize: 7, lineHeight: 9, color: COLORS.medium, maxWidth: CONTENT_WIDTH - 12 });
          y -= 3;
        }

        // Slide / Line
        if (action.slide_name || action.line_item) {
          const meta = [action.slide_name ? `Slide: ${sanitize(action.slide_name)}` : "", action.line_item ? `Line: ${sanitize(action.line_item)}` : ""].filter(Boolean).join(" | ");
          page.drawText(meta, { x: MARGIN + 4, y, font, size: 6, color: COLORS.light });
          y -= 9;
        }

        // Comments — show latest 3
        const comments = action.comments || [];
        if (comments.length > 0) {
          page.drawText(`Comments (${comments.length}):`, { x: MARGIN + 4, y, font: fontBold, size: 6, color: COLORS.accent });
          y -= 9;
          const showComments = comments.slice(-3); // last 3
          for (const c of showComments) {
            if (y < 30) break;
            const cText = sanitize(`${c.author || "?"} (${sanitize(c.timestamp || "")}): ${c.text || ""}`);
            y = drawWrapped(page, cText, MARGIN + 8, y, { fontSize: 6, lineHeight: 8, color: COLORS.medium, maxWidth: CONTENT_WIDTH - 12 });
            y -= 2;
          }
        }

        y -= 8;
        // Separator
        if (y > MARGIN && idx < activeActions.length - 1) {
          page.drawRectangle({ x: MARGIN, y, width: CONTENT_WIDTH, height: 0.3, color: COLORS.border });
          y -= 8;
        }
      }
    }
  }

  // ═══════════════════════════════════════════
  // 4. REPORTS — Compact callout strip + PDF pages
  // ═══════════════════════════════════════════
  notify("Merging report PDFs...");
  for (const { key, label, pdfUrl, calloutText } of reportData) {
    const hasCallout = calloutText && calloutText.trim();
    const hasPdf = !!pdfUrl;
    if (!hasPdf && !hasCallout) continue;

    if (hasCallout) {
      const safeCallout = sanitize(calloutText);
      const SF = 4.5, SLH = 6, SM = 15;
      const mtw = PAGE_WIDTH - SM * 2 - 6;
      const words = safeCallout.split(" ");
      let lc = 1, cl = "";
      for (const w of words) { const t = cl ? `${cl} ${w}` : w; if (font.widthOfTextAtSize(t, SF) > mtw && cl) { lc++; cl = w; } else cl = t; }
      const maxL = Math.floor(108 / SLH);
      const sh = Math.min(130, 22 + Math.min(lc, maxL) * SLH);
      let dt = safeCallout;
      if (lc > maxL) { const ac = Math.floor(mtw / (SF * 0.45)); dt = safeCallout.substring(0, ac * maxL - 3) + "..."; }
      const sp = doc.addPage([PAGE_WIDTH, sh]);
      sp.drawRectangle({ x: 0, y: sh - 2, width: PAGE_WIDTH, height: 2, color: COLORS.accent });
      sp.drawText(`${sanitize(label)} - Callout`, { x: SM, y: sh - 13, font: fontBold, size: 6, color: COLORS.accent });
      drawWrapped(sp, dt, SM + 3, sh - 21, { fontSize: SF, lineHeight: SLH, color: COLORS.medium, maxWidth: mtw });
    }

    if (hasPdf) {
      try {
        notify(`Merging ${label}...`);
        const r = await fetch(pdfUrl);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const b = await r.arrayBuffer();
        const ext = await PDFDocument.load(b, { ignoreEncryption: true });
        const cp = await doc.copyPages(ext, ext.getPageIndices());
        for (const p of cp) {
          const { width: ow, height: oh } = p.getSize();
          if (Math.abs(ow - PAGE_WIDTH) < 5) { doc.addPage(p); }
          else { const s = PAGE_WIDTH / ow; const nh = oh * s; const np = doc.addPage([PAGE_WIDTH, nh]); const em = await doc.embedPage(p); np.drawPage(em, { x: 0, y: 0, width: PAGE_WIDTH, height: nh }); }
        }
      } catch (err) {
        const ep = doc.addPage([PAGE_WIDTH, 80]);
        ep.drawText(`Failed: ${sanitize(label)} - ${sanitize(err.message)}`, { x: MARGIN, y: 50, font, size: 8, color: COLORS.danger });
      }
    }
  }

  // ─── Download ───
  notify("Saving PDF...");
  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `WBR_Pack_${functionId}_W${week}_${year}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  notify("Done!");
  return true;
}
